import React, { forwardRef } from "react";
import * as Primitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";

const root = cva("relative flex shrink-0 overflow-hidden rounded-full bg-neutral-100", {
  variants: {
    size: {
      sm: "h-8 w-8",
      base: "h-10 w-10",
      lg: "h-14 w-14",
    },
  },
  defaultVariants: { size: "base" },
});

type AvatarProps = React.ComponentPropsWithoutRef<typeof Primitive.Root> & VariantProps<typeof root>;

export const Avatar = forwardRef<React.ElementRef<typeof Primitive.Root>, AvatarProps>(
  ({ size, className = "", ...props }, ref) => (
    <Primitive.Root ref={ref} className={cn(root({ size }), className)} {...props} />
  )
);
Avatar.displayName = "Avatar";

export const AvatarImage = forwardRef<
  React.ElementRef<typeof Primitive.Image>,
  React.ComponentPropsWithoutRef<typeof Primitive.Image>
>(({ className = "", ...props }, ref) => (
  <Primitive.Image ref={ref} className={cn("aspect-square h-full w-full object-cover", className)} {...props} />
));
AvatarImage.displayName = "AvatarImage";

export const AvatarFallback = forwardRef<
  React.ElementRef<typeof Primitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof Primitive.Fallback>
>(({ className = "", ...props }, ref) => (
  <Primitive.Fallback
    ref={ref}
    className={cn("flex h-full w-full items-center justify-center text-sm font-medium text-text-muted", className)}
    {...props}
  />
));
AvatarFallback.displayName = "AvatarFallback";
